import {
  Component,
  input,
  output,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  AlertService, ButtonLoadingDirective,
  CardComponent,
  CardContentComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { RedemptionAndPayment } from '../../../../core/payments-common';
import { BlockchainUrlPipe } from '../../../../pipes/blockchain-url.pipe.js';
import { RedemptionDialogHeaderComponent } from '../redemption-dialog-header/redemption-dialog-header.component.js';
import { CurrencyConvertPipe } from '../../../../pipes/currency-convert.pipe.js';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LottieAnimationComponent } from '../../../lottie-animation/lottie-animation.component.js';
import { decimal } from '@gains-com/shared';
import { CurrenciesViewService } from '../../../../core/view-states/currencies-view.service.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { DecimalPipe } from '../../../../pipes/decimal.pipe.js';
import { MatButton } from '@angular/material/button';
import { RedemptionsService } from '../../../../core/redemption.service.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';

@Component({
  selector: 'app-crypto-confirm-redemption',
  imports: [
    CardComponent,
    CardContentComponent,
    RedemptionDialogHeaderComponent,
    DecimalPipe,
    CurrencyPipe,
    BlockchainUrlPipe,
    IconComponent,
    CurrencyConvertPipe,
    AsyncPipe,
    MatProgressSpinner,
    DatePipe,
    LottieAnimationComponent,
    MatButton,
    ButtonLoadingDirective,
  ],
  templateUrl: './crypto-confirm-redemption.component.html',
  styleUrls: ['../styles.scss', './crypto-confirm-redemption.component.scss'],
})
export class CryptoConfirmRedemptionComponent {
  private readonly _logger = inject(LOGGER_FACTORY).create('RedemptionsDialog');
  private readonly _alert = inject(AlertService);

  step = input<number>(2);
  redemption = input.required<RedemptionAndPayment>();
  private readonly _redemptionsService = inject(RedemptionsService);
  currenciesView = inject(CurrenciesViewService);
  currencies = toSignal(this.currenciesView.currencies$);

  currentCurrency = computed(
    () =>
      this.currencies()!.find(
        (c) => c.currency_id === this.redemption().currency_id,
      )!,
  );
  totalRedemptionAmount = computed(() =>
    decimal(this.redemption().amount)
      .minus(this.redemption().transaction_fee_amount ?? 0)
      .toNumber(),
  );
  blockedSubtitle = computed(
    () =>
      'Your redemption has been submitted and is currently under review. All redemptions are reviewed by our team and are typically processed the same day they are submitted.',
  );

  cancelInProgress = signal(false);

  setAmount = output<number>();
  close = output<'contact-support' | 'additional-verification' | null>();
  cancel = output<void>();

  async cancelRedemption() {
    const redemption = this.redemption();
    if (!redemption) return;

    this.cancelInProgress.set(true);

    try {
      await this._redemptionsService.cancelRedemption(redemption.redemption_id);
    } catch (error: any) {
      if (error?.code !== 'INVALID_REDEMPTION_STATUS') {
        this._logger.error('Failed to cancel redemption', {
          error: error.message,
          redemptionId: redemption.redemption_id,
        });
        this._alert.notifyDanger(
          'Cancellation Failed',
          'Unable to cancel redemption. Please try again or contact support.',
        );
      }
    } finally {
      this.cancelInProgress.set(false);
    }
  }
}
